import { DOCUMENT } from '@angular/common';
import { Component, DestroyRef, Inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, delay, filter, switchMap } from 'rxjs';

import { DEBOUNCE_TIMEOUT } from '@shared-constants/ui-constants';
import { SHAREPOINT_FQDN_URL } from '@shared-constants/url-constants';
import { CatsNoBillCodeDescriptionEnum } from '@shared-enums/cats.enum';
import { LocalStorageKeyEnum } from '@shared-enums/local-storage.enum';
import { TimesheetStatusEnum } from '@shared-enums/time.enum';
import { findUnique } from '@shared-helpers/array-object-helpers';
import { checkLocalStorage, updateLocalStorage } from '@shared-helpers/storage-helpers';
import { safeCompare, trimLeadingZeros } from '@shared-helpers/string-number-helpers';
import { IHttpResponseEquipment, IWebServerResponseApprover } from '@shared-interfaces/api';
import { IDayOfWeek } from '@shared-interfaces/dates';
import { IExtDocument } from '@shared-interfaces/documents';

import { environment } from '../../../environments/environment';
import { IModalContentCreateOrEditTimesheetResult } from '../../components/modal/modal-content-timesheet-create-or-edit/modal-content-timesheet-create-or-edit.component';
import { INumberBoxClickedEvent, ITableLikeLayoutEvent } from '../../components/table-like-layout/table-like-layout.component';
import { TabsComponent } from '../../components/tabs/tabs.component';
import { ICatRecord } from '../../interfaces/incoming';
import { ITextClickEvent } from '../../interfaces/internal-interfaces';
import { IAnyInterfaceType } from '../../scripts/basicInterface';
import { BillingTimesheet, ISharepointDisplayData } from '../../scripts/BillingTimesheet';
import { Employee } from '../../scripts/employee';
import { Equipment } from '../../scripts/equipment';
import { ExtDocument } from '../../scripts/extDocument';
import { ProductionGroup } from '../../scripts/Production-Group/ProductionGroup';
import { ProductionEquipment } from '../../scripts/productionEquipment';
import { ProductionQuantity } from '../../scripts/ProductionQuantity';
import { ProductionText } from '../../scripts/ProductionText';
import {
  billingEmployeeWeeklyConfig,
  billingProductionTextWeeklyConfig,
  makeBillingAttachmentConfig,
  makeBillingEquipmentWeeklyConfig,
  makeBillingProductionQuantityWeeklyConfig,
} from '../../scripts/table-configs/billing-tables.config';
import { ITimesheetDate } from '../../scripts/timesheetHandler';
import { DatahubService } from '../../services/datahub.service';
import { IApplicationQueryParams } from '../../services/datahub.service';
import { MessageService } from '../../services/message.service';
import { ModalService } from '../../services/modal.service';
import { TimesheetHelperService } from '../../services/timesheet-helper.service';
import { ILayoutConfiguration } from './billing-review.component.model';

export interface ITabProps {
  displayProperty1: string;
  displayProperty2: string;
}

@Component({
  selector: 'billing-review',
  templateUrl: './billing-review.component.html',
  standalone: false,
})
export class BillingReviewComponent implements OnInit {
  @ViewChild('timesheetTabsEl') public readonly timesheetTabsEl?: TabsComponent<string>;

  public sectionTabs: string[] = [];
  public activeSectionTabIndex = 1;
  public selectedSectionTabName = '';
  public timesheetTabs: ITabProps[] = [];
  public activeTimesheetTabIndex = 0;
  public isViewingSingleTimesheet = false;
  public isAttachButtonEnabled = false;
  public configsAndValues: Array<
    | ILayoutConfiguration<ExtDocument>
    | ILayoutConfiguration<Employee>
    | ILayoutConfiguration<Equipment | ProductionEquipment>
    | ILayoutConfiguration<ProductionQuantity>
    | ILayoutConfiguration<ProductionGroup>
    | ILayoutConfiguration<ISharepointDisplayData>
  > = [];
  public timesheet: BillingTimesheet = this.datahub.billingTimesheet;
  public currentTimesheets: ExtDocument[] = [];
  public timesheetDesc = '';
  public dates: ITimesheetDate[] = [];
  public billingProductionTextConfig = billingProductionTextWeeklyConfig;
  public billingProductionTextValues: ProductionText[] = [];
  public isWholeContract = false;
  public wholeContractText = 'View Whole Contract';
  public currentSharepointUploadData?: ExtDocument = undefined;
  public contractDescription = '';
  public isSplitScreen = false;
  public isLoadingProdGroup = false;

  private activeTimesheetTab = '';
  private equipment: Array<Equipment | ProductionEquipment> = [];
  private approvingName?: string;
  private serviceOrder = '';
  private readonly prodGroupChangeDebounceSubject$ = new Subject<ITableLikeLayoutEvent<ProductionGroup>>();

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly datahub: DatahubService,
    private readonly modalService: ModalService,
    private readonly messageSvc: MessageService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly messageService: MessageService,
    private readonly timesheetHelperSvc: TimesheetHelperService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  ngOnInit(): void {
    this.datahub.switchApprover$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (x) => {
        this.isWholeContract = false;
        this.switchForeman(x);
      },
      error: (err) => {
        console.error('Error in switchApprover$ subscription:', err);
      },
    });

    this.activatedRoute.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (params) => {
        let typedParams = params as IApplicationQueryParams; // we expect/know the params will have certain properties, so we force the type so we can more easily work with it
        if (typedParams.approver === 'all' && !this.approvingName) {
          typedParams = checkLocalStorage<IApplicationQueryParams>(LocalStorageKeyEnum.WorkingAs);
          this.datahub.updateQueryParams(typedParams);
        }
        this.readQueryParams(typedParams);
      },
      error: (err) => {
        console.error('Error in queryParams subscription:', err);
      },
    });

    this.timesheet.ready$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (isReady) => {
        if (!isReady) return;
        this.equipment = [].concat(this.timesheet.equipment).concat(this.timesheet.productionEquipment);
        this.configsAndValues = [
          {
            values: this.timesheet.extDocuments,
            shouldShowHeaders: true,
            display: 'Headers',
            isSplitScreen: false,
          },
          {
            config: billingEmployeeWeeklyConfig,
            values: this.timesheet.employees,
            valuePath: 'billingGrid',
            shouldShowTotal: true,
            appearance: 'bars',
            display: 'Employees',
          },
          {
            config: makeBillingEquipmentWeeklyConfig(false),
            values: this.equipment,
            valuePath: 'billingGrid',
            shouldShowTotal: true,
            appearance: 'bars',
            display: 'Equipment',
          },
          {
            config: makeBillingProductionQuantityWeeklyConfig(false),
            values: this.timesheet.productionQuantity,
            valuePath: 'billingGrid',
            shouldShowProductionText: true,
            appearance: 'bars',
            display: 'Production',
          },
          {
            config: this.timesheet.contractWrapper.getPrdGrpConfig(),
            values: this.timesheet.productionGroups,
            appearance: 'bars',
            shouldShowTotal: true,
            valuePath: null,
            isSplitScreen: false,
            shouldShowProductionText: false,
            display: 'Groups',
            changeFunction: this.handleProductionGroupItemChange.bind(this),
            addFunction: this.handleAddGroupClicked.bind(this),
          },
          {
            config: makeBillingAttachmentConfig(),
            values: this.timesheet.sharepointFiles.sort(),
            valuePath: null,
            appearance: 'attachment',
            display: 'Attachments',
            changeFunction: this.handleSharepointNameChange.bind(this),
          },
        ];
        if (!this.timesheet.contractWrapper.prdGroupExists()) {
          //remove groups if there are no prod groups on the contract
          this.configsAndValues = this.configsAndValues.filter((x) => x.display !== 'Groups');
        }
        this.dates = this.timesheet.getWeek().map((d) => {
          //modify the display property for the UI on this page - nothing we pass it to later will need this in the older format
          d.display = d.moment.format('MM/DD');
          return d;
        });
        this.sectionTabs = this.configsAndValues.map((x) => x.display);
        this.setTimesheetTabs();
        if (this.approvingName === 'All') {
          this.contractDescription = this.timesheet.contractWrapper.contractName;
          this.timesheet.disableApproval('billing', 'Approval cannot be done while viewing the whole contract.');
        }
        if (this.timesheet.timesheetStatus.number === TimesheetStatusEnum.BillingApproved) {
          this.timesheet.disableApproval(
            'billing',
            `All billing records approved, ${this.timesheet.timesheetStatus.description}`,
          );
        }
        this.billingProductionTextValues = this.timesheet.productionText;
        this.handleTimesheetChange(this.timesheetTabs[this.timesheetTabs.length - 1]);
        this.setActive(this.sectionTabs[1]);
      },
      error: (err) => {
        console.error('Error in timesheet.ready subscription:', err);
      },
    });

    this.timesheetHelperSvc.timesheetCreated$.subscribe({
      next: (newTimesheet) => {
        // Insert the new timesheet as an object with displayName1 and displayName2
        const newTab = {
          displayProperty1: trimLeadingZeros(newTimesheet.Documentno),
          displayProperty2: newTimesheet.Description || 'No Description',
        };

        // Insert the new tab before the "All" tab (next to last position)
        this.timesheetTabs.splice(this.timesheetTabs.length - 1, 0, newTab);

        if (this.activeTimesheetTab === '') {
          // If the active tab is "All", update the activeTimesheetTabIndex to the new position of "All"
          this.activeTimesheetTabIndex = this.timesheetTabs.length - 1;
        }

        if (this.timesheetTabsEl) {
          // Manually trigger ngOnChanges to update the UI
          //eslint-disable-next-line @angular-eslint/no-lifecycle-call -- Legacy support, we need to do this since there's no other good way to make it happen right now
          this.timesheetTabsEl.ngOnChanges();
        }
      },
      error: (err) => {
        console.error('Error in timesheetCreated$ subscription:', err);
      },
    });

    this.timesheetHelperSvc.recordsMoved$.pipe(delay(50)).subscribe({
      next: () => {
        //When a record is moved by the service, we need to refresh the UI/view.
        //We can do this by re-selecting the tab that is already active.
        //NOTE: this must be done on a slight delay or else the UI will not properly update

        //We must first get which tab is selected, then re-determine which tabs need to show
        let currentlySelectedTab = this.timesheetTabs[this.activeTimesheetTabIndex];
        //If the tab is still present in the array, keep it selected. If not then just select the last ALL tab instead

        this.setTimesheetTabs();
        currentlySelectedTab =
          this.timesheetTabs.findIndex((t) => t === currentlySelectedTab) === -1
            ? this.timesheetTabs[this.timesheetTabs.length - 1]
            : currentlySelectedTab;
        this.handleTimesheetChange(currentlySelectedTab);
      },
      error: (err) => {
        console.error('Error in recordsMoved$ subscription:', err);
      },
    });

    this.prodGroupChangeDebounceSubject$.pipe(debounceTime(DEBOUNCE_TIMEOUT)).subscribe({
      next: (event) => {
        const item = event.entity.getTextOrQuantityByStringPropertyName(event.columnConfig.nestedObjectName);
        item.handleChange(this.datahub.catsDB);
        this.timesheet.updateExtDocumentStatus([item.Extdocumentno]);
      },
      error: (err) => {
        console.error('Error in prodGroupChangeDebouncer$ subscription:', err);
      },
    });
  }

  private readQueryParams(params: IApplicationQueryParams): void {
    if (params.wedate) this.timesheet.weDate.setWeDateWithString(params.wedate, true);
    this.serviceOrder = params.order;
    const order$ = this.timesheet.setServiceOrder$(params.order);
    const approver = params.approver ?? this.datahub.user.approvingId;
    let approver$: Observable<string | IWebServerResponseApprover> = of(approver);
    if (!params.order) {
      this.datahub.navigate('/contract');
      this.datahub.messageService.warning('You must select a valid contract.');
    }
    if (params.approver && params.approver !== 'all') {
      updateLocalStorage(LocalStorageKeyEnum.WorkingAs, params);
    }
    if (!safeCompare(approver, this.datahub.user.approvingId)) {
      //if the "approver" in the user object is not the same as the one in the query params, queue up the transition
      approver$ = this.datahub.user.switchApprover$(params.approver);
    }
    if (approver === 'all') {
      //no need to look for approver object if we are doing whole contract
      order$.subscribe({
        next: () => {
          this.isWholeContract = true;
          this.wholeContractText = 'Back To My Page';
          this.approvingName = 'All';
          this.timesheet.reset(false);
        },
        error: (err: unknown) => {
          this.displayContractError(err);
        },
      });
    } else {
      combineLatest([order$, approver$]).subscribe({
        next: () => {
          this.wholeContractText = 'View Whole Contract';
          this.approvingName = this.datahub.user.approvingName;
          this.timesheet.reset(true);
        },
        error: (err: unknown) => {
          this.displayContractError(err);
        },
      });
    }
  }

  private displayContractError(err: unknown): void {
    const msg =
      err instanceof Error ? err.message : 'There was an issue loading the data for this contract. Please contact Davey Time.';
    this.messageService.error(msg);
  }

  setActive(tab: string) {
    this.datahub.detailPayneHandler.showSummary();
    this.activeSectionTabIndex = this.sectionTabs.findIndex((x) => x === tab);
    this.selectedSectionTabName = this.sectionTabs[this.activeSectionTabIndex];
    this.isSplitScreen = this.configsAndValues.find((x) => x.display === tab).isSplitScreen ?? true; //default to true if it is not defined
  }

  handleTextClick(event: ITextClickEvent<IAnyInterfaceType>): void {
    if (!event.config.shouldDetectClick) return null;
    if (event.config.clickValue === 'horizontalBulkEntry' && !(event.entity instanceof ProductionText)) {
      event.entity.showBulkAdd();
      return;
    }
    if (event.config.clickValue === 'billcodeChange') {
      this.launchBillCodeChange(event);
    }
  }

  public handleIconClicked<T>(event: ITableLikeLayoutEvent<T>): void {
    if (!event.columnConfig.shouldDetectClick) return;
    if (event.columnConfig.clickValue === 'deleteClicked') {
      this.isLoadingProdGroup = true;
      this.timesheet
        .removeProductionGroup$(event.entity as ProductionGroup)
        .subscribe({ next: () => (this.isLoadingProdGroup = false) });
    } else if (event.columnConfig.clickValue === 'moveTimeSheetClicked') {
      this.handleMoveProductionGroupToTimeSheet(event.entity as ProductionGroup);
    } else if (event.columnConfig.clickValue === 'deleteSharepointFile') {
      const weekendingDate = this.timesheet.weDate.getSharepointFormat();
      this.timesheet.deleteSharepointFile(weekendingDate, event.entity as ISharepointDisplayData);
    } else if (event.columnConfig.clickValue === 'visitSharepointUrl') {
      this.openWebPage(event.entity as ISharepointDisplayData);
    }
  }

  handleValueChange<T>(event: ITableLikeLayoutEvent<T>, layoutConfig: ILayoutConfiguration<T>) {
    if (layoutConfig.changeFunction) {
      layoutConfig.changeFunction(event);
    }
  }

  private handleSharepointNameChange(event: ITableLikeLayoutEvent<ISharepointDisplayData>) {
    const selectedSharepointRow = event.entity;
    const fileType: string = selectedSharepointRow.fileType;

    //Display name is either a string or a string array. We need it to become a string in all cases
    const displayName: string =
      typeof selectedSharepointRow.displayName === 'string'
        ? selectedSharepointRow.displayName //just use the string name
        : selectedSharepointRow.displayName.join(''); //join all array parts together if the display name is an array

    if (displayName + fileType === selectedSharepointRow.fileName) return;

    selectedSharepointRow.hasBeenUploaded = false;
    selectedSharepointRow.canDelete = false;
    selectedSharepointRow.canView = false;
    const weekendingDate = this.timesheet.weDate.getSharepointFormat();
    this.timesheet.updateSharepointFile(weekendingDate, selectedSharepointRow);
  }

  private handleProductionGroupItemChange(event: ITableLikeLayoutEvent<ProductionGroup>) {
    this.prodGroupChangeDebounceSubject$.next(event);
  }

  handleAddGroupClicked(changeEvent: MatButtonToggleChange) {
    const day = changeEvent.value as ITimesheetDate;
    this.isLoadingProdGroup = true;

    let timesheetNum = this.activeTimesheetTab;

    // If no tab is selected and there are only two tabs (a timesheet tab and "All"), use the first tab's full timesheet number
    if (timesheetNum === '' && this.timesheetTabs.length === 2) {
      timesheetNum = this.getFullTimesheetNumber(this.timesheetTabs[0].displayProperty1);
    }

    this.timesheet.addProductionGroup$(day.unix, timesheetNum).subscribe({
      complete: () => {
        this.isLoadingProdGroup = false;
        changeEvent.source.buttonToggleGroup.value = null; // Deselect the clicked button
      },
    });
  }

  public openWebPage(entity: ISharepointDisplayData): void {
    if (entity.url.includes(SHAREPOINT_FQDN_URL)) this.document.defaultView?.open(`${entity.url}?web=1`);
    else this.document.defaultView?.open(`${environment.sharepointBaseUrl}${entity.url}?web=1`);
  }

  private launchBillCodeChange(event: ITextClickEvent<IAnyInterfaceType>): void {
    const type = event.entity.billCodeType;
    const codes = this.timesheet.contractWrapper.getBillCodesByType(type);
    const options = {
      selectOptions: codes,
      current: event.value,
    };

    const modal = this.modalService.launchResponsiveModal('billcode-change', options, { autoFocus: 'first-tabbable' });

    modal
      .afterClosed()
      .pipe(
        filter((newBillCode: string | null) => newBillCode !== '' && newBillCode != null), // don't do anything if nothing was chosen
        switchMap((newBillCode) => {
          if (event.entity instanceof ProductionText) {
            console.error('Billing codes cannot be changed for Production Text!');
            return of(null);
          } else {
            // For all entity types except for production text
            if (options.current === CatsNoBillCodeDescriptionEnum.Unassigned) options.current = '';
            const timesheetNo = this.activeTimesheetTab;
            return event.entity.handleMassBillCodeChange$(options.current, newBillCode, timesheetNo);
          }
        }),
        filter((records) => records != null),
      )
      .subscribe({
        next: (records) => {
          const documents = findUnique<string>(records, 'Extdocumentno', false);
          this.datahub.billingTimesheet.updateExtDocumentStatus(documents);
          this.datahub.detailPayneHandler.showSummary();
        },
        error: (err) => {
          console.error('Error in handleMassBillCodeChange subscription:', err);
        },
      });
  }

  changeDetailPayne(event: INumberBoxClickedEvent<IAnyInterfaceType, IDayOfWeek>) {
    event.entity.getBillingDetailPayneValues(event.row, event.column);
  }

  handleWeDateChanged() {
    this.handleTimesheetChange(this.timesheetTabs[0]);
    this.timesheet.navigateToSelf(this.datahub.user.approvingId);
  }

  handleWholeContractClick() {
    if (this.isWholeContract) this.timesheet.navigateToSelf();
    else this.timesheet.navigateToSelf('all');
    this.toggleWholeContract();
  }

  private toggleWholeContract() {
    if (this.isWholeContract) {
      this.isWholeContract = false;
      this.wholeContractText = 'View Whole Contract';
    } else {
      this.isWholeContract = true;
      this.wholeContractText = 'Back to My Page';
    }
  }

  public addEquipment(): void {
    this.modalService
      .launchResponsiveModal('entity-number-lookup', { entityLookupType: 'equipment' }, { autoFocus: 'first-tabbable' })
      .afterClosed()
      .pipe(filter((val: IHttpResponseEquipment | undefined) => val != null))
      .subscribe({
        next: (value: IHttpResponseEquipment) => {
          const addedWithoutHours = this.timesheet.equipment.find((equip) => safeCompare(equip.EquipmentId, value.EquipmentId));
          const wasAlreadyAdded = addedWithoutHours !== undefined;

          if (wasAlreadyAdded && this.isViewingSingleTimesheet) {
            if (addedWithoutHours.billingTotal === '0') {
              addedWithoutHours.shouldShow = true;
            } else {
              this.messageService.info(`Equipment "<strong>${value.Description}</strong>" is already included on this timesheet`);
            }
          } else {
            const equipmentItem = new Equipment(value, this.datahub, 'billing');
            equipmentItem.setDefaultCode(this.timesheet.contractWrapper);
            equipmentItem.calculateBillingGrid(); // the billing grid will not come pre-populated.
            // Add it at the start of the array
            this.timesheet.equipment.unshift(equipmentItem);
            this.equipment.unshift(equipmentItem);
          }
        },
        error: (err) => {
          console.error('Error in afterClosed subscription:', err);
        },
      });
  }

  switchForeman(approver: IWebServerResponseApprover) {
    this.approvingName = approver.display;
    this.timesheet.navigateToSelf(approver.value);
  }

  public async approve(): Promise<void> {
    if (this.timesheet.checkNoContractRecords()) {
      this.handleNoContractCase();
    } else if (this.timesheet.timesheetStatus.number === TimesheetStatusEnum.Created) {
      this.timesheet.handleNoHoursCase();
    } else if (this.timesheet.checkUnassignedRecords()) {
      this.handleUnassignedCase();
    } else if (this.timesheet.checkApprovedPayroll()) {
      await this.timesheet.approve(false);
    } else {
      await this.timesheet.handleUnapprovedCase();
    }
  }

  private handleUnassignedCase(): void {
    this.modalService.launchResponsiveModal('simple-text', {
      title: 'Fix Unassigned Records',
      body: 'This page contains records with unassigned bill codes.\n\nAssign each record a bill code before billing approval.',
    });
  }

  private handleNoContractCase(): void {
    this.modalService.launchResponsiveModal('simple-text', {
      title: 'Fix No Contract Records',
      body: 'This page contains records with bill codes that are not on this contract.\n\nAssign each record a bill code before billing approval.',
    });
  }

  public handleTimesheetChange(tab: ITabProps): void {
    const tabText = tab.displayProperty1;
    this.activeTimesheetTab = tabText === 'All' ? '' : this.getFullTimesheetNumber(tabText);
    this.isViewingSingleTimesheet = false;

    const found = this.timesheet.extDocuments.find((x) => safeCompare(x.Documentno, tabText));

    if (tabText === 'All') {
      this.timesheet.processTimesheetChange(this.activeTimesheetTab);
      this.activeTimesheetTabIndex = this.timesheetTabs.length - 1;
      this.isViewingSingleTimesheet = this.timesheetTabs.length === 2; //if we only have one + the "all" tab, just treat the UI as if the one is selected

      this.currentSharepointUploadData = this.currentTimesheets.length === 1 ? this.timesheet.extDocuments[0] : undefined;

      this.currentTimesheets = this.timesheet.extDocuments;
      //If there's only one timesheet, show that title instead of the "all" title
      this.timesheetDesc = this.currentTimesheets.length === 1 ? this.currentTimesheets[0].Description : 'Viewing all timesheets';
    } else if (found) {
      this.isViewingSingleTimesheet = true;
      this.timesheet.processTimesheetChange(this.activeTimesheetTab);
      this.activeTimesheetTabIndex = this.timesheetTabs.findIndex((x) => x.displayProperty1 === tabText);
      this.currentSharepointUploadData = found;
      this.currentTimesheets = [found];
      this.timesheetDesc = found.Description;
    }

    this.isAttachButtonEnabled = !this.isWholeContract && (this.isViewingSingleTimesheet || this.currentTimesheets.length === 1);
    this.datahub.recalcTotals();
    this.timesheet.viewFinished();
  }

  private getFullTimesheetNumber(num: string): string {
    //Add prefixed zeros that the API needs, but we remove them in the UI for display purposes
    return num.padStart(7, '0');
  }

  private setTimesheetTabs(): void {
    if (this.approvingName === 'All') {
      this.timesheetTabs = this.timesheet.extDocuments
        .filter((document) => document.Salesdoc === this.serviceOrder && this.shouldIncludeDocumentInTabs(document))
        .map((document) => ({
          displayProperty1: trimLeadingZeros(document.Documentno),
          displayProperty2: document.Description || 'No Description',
        }))
        .concat([{ displayProperty1: 'All', displayProperty2: 'All' }]);
    } else {
      this.timesheetTabs = this.timesheet.extDocuments
        .filter((document) => this.shouldIncludeDocumentInTabs(document))
        .map((document) => ({
          displayProperty1: trimLeadingZeros(document.Documentno),
          displayProperty2: document.Description || 'No Description',
        }))
        .concat([{ displayProperty1: 'All', displayProperty2: 'All' }]);
    }
  }

  /**@description Includes the passed document in the tabs if there are any records OR if there are any sharepoint files  */
  private shouldIncludeDocumentInTabs(document: ExtDocument): boolean {
    return (
      this.timesheet.catsRecords.some((cat) => safeCompare(cat.Extdocumentno, document.Documentno)) ||
      this.timesheet.sharepointFiles.some((file) => safeCompare(file.timesheetNo, document.Documentno))
    );
  }

  private handleMoveProductionGroupToTimeSheet(prodGroup: ProductionGroup): void {
    this.modalService
      .launchResponsiveModal('timesheet-move', { productionGroup: prodGroup, timesheet: this.timesheet })
      .afterClosed()
      .pipe(
        filter((x) => x), // remove empty, happens when modal is closed without selecting a place to move it to
      )
      .subscribe({
        next: (result: IModalContentCreateOrEditTimesheetResult | ExtDocument) => {
          if (result instanceof ExtDocument) {
            // Move the record to an existing document
            this.handleMoveProductionGroup(this.timesheet, result, prodGroup);
          } else if (Array.isArray(result.headers)) {
            // Create a new document and then move the record there
            this.handleCreateTimesheetAndMoveProductionGroup(this.timesheet, result, prodGroup);
          } else {
            // Shouldn't happen, but just in case!
            this.messageSvc.error(
              'Unable to move this production group because there was a problem finding where to move it to',
              'Record Not Moved',
            );
          }
        },
      });
  }

  private handleMoveProductionGroup(
    billingTimesheet: BillingTimesheet,
    targetDocument: ExtDocument,
    prodGroup: ProductionGroup,
    newlyCreatedDocument?: ExtDocument,
  ) {
    const prevDocumentNo = prodGroup.ExtDocumentno;
    this.timesheetHelperSvc.moveAllProductionGroupRecords$(billingTimesheet, targetDocument, prodGroup).subscribe({
      next: (result) => {
        this.handleUpdateViewAfterProductionGroupMove(
          result.productionGroup,
          result.records,
          prevDocumentNo,
          newlyCreatedDocument,
        );
      },
      error: (err: unknown) => {
        const msg =
          newlyCreatedDocument instanceof ExtDocument
            ? `The new timesheet <strong>#${trimLeadingZeros(newlyCreatedDocument.Documentno)}</strong> was created, but there was an error moving the selected record into it!`
            : `There was an error moving the record to the selected timesheet`;
        this.messageSvc.error(msg, 'Record Not Moved');
        console.error(err);
      },
    });
  }

  private handleCreateTimesheetAndMoveProductionGroup(
    billingTimesheet: BillingTimesheet,
    newTimesheetResult: IModalContentCreateOrEditTimesheetResult,
    prodGroup: ProductionGroup,
  ) {
    this.timesheetHelperSvc
      .create$(
        billingTimesheet.contractWrapper,
        newTimesheetResult.description,
        newTimesheetResult.headers,
        TimesheetStatusEnum.PayrollApproved,
      )
      .subscribe({
        next: (createdTimesheet: IExtDocument) => {
          const newDocument = new ExtDocument(createdTimesheet, this.datahub, false);
          billingTimesheet.addExtDoc(newDocument);
          this.handleMoveProductionGroup(billingTimesheet, newDocument, prodGroup, newDocument);
        },
        error: (err: unknown) => {
          this.messageSvc.error(`There was an error creating the new timesheet`);
          console.error(err);
        },
      });
  }

  private handleUpdateViewAfterProductionGroupMove(
    updatedProductionGroup: ProductionGroup,
    updatedRecordsList: ICatRecord[],
    prevDocNo: string,
    newlyCreatedDocument?: ExtDocument,
  ) {
    //When we get a response back after the modal has closed, the data has been moved but we need to update the UI to match
    const recordsNotMoved = updatedRecordsList.filter((r) => r.Extdocumentno === prevDocNo);
    updatedProductionGroup.checkApprovalStatus(updatedRecordsList);

    if (recordsNotMoved.length === updatedRecordsList.length) {
      //Total failure, no records were moved!
      const msg =
        newlyCreatedDocument instanceof ExtDocument
          ? `The new timesheet <strong>#${newlyCreatedDocument.Documentno}</strong> was created, but the selected Production Group was not able to be moved to it!`
          : `The selected Production Group was not able to be moved!`;
      this.messageSvc.error(msg, 'Production Group Not Moved');
    } else if (recordsNotMoved.length > 0) {
      //Mixed success/failure, not all records moved! - Hopefully this will never happen
      const notAllMovedMsg = `${recordsNotMoved.length.toString()} of the ${updatedRecordsList.length.toString()} records on the ProductionGroup could not be moved. You might have to try again to move them all`;
      const msg =
        newlyCreatedDocument instanceof ExtDocument
          ? `The new timesheet <strong>#${newlyCreatedDocument.Documentno}</strong> was created, but ${notAllMovedMsg}`
          : notAllMovedMsg;
      this.messageSvc.error(msg, 'Not All Records Moved');
    } else if (recordsNotMoved.length === 0) {
      //Total success, all records moved!
      const msg =
        newlyCreatedDocument instanceof ExtDocument
          ? `The new timesheet <strong>#${newlyCreatedDocument.Documentno}</strong> was created, and the selected Production Group has been moved to it`
          : `The selected Production Group has been moved to the selected timesheet`;
      this.messageSvc.success(msg, 'Production Group Moved');
    }
  }
}
